import { create } from 'zustand'
import {
  api,
  getCachedUser,
  getToken,
  isOffline,
  setCachedUser,
  setToken,
} from '@/services/api'
import { ssoClient } from '@/services/sso'
import { isDesktopApp } from '@/services/platform'
import { ensureAgentRunning } from '@/services/agent'
import type { SsoConfig, User } from '@/services/types'

interface AuthState {
  user: User | null
  status: 'loading' | 'authenticated' | 'anonymous'
  error: string | null
  ssoConfig: SsoConfig | null
  /** Signed in from a cached session because the API could not be reached. */
  offline: boolean
  /** Set right after an SSO sign-up, while the account still needs verifying. */
  pendingVerificationEmail: string | null
  restore: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  register: (email: string, name: string, password: string) => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (token: string, newPassword: string) => Promise<void>
  resendVerification: (email: string) => Promise<void>
  /** Exchanges a token handed back by the SSO's own redirect flows. */
  completeSsoExchange: (ssoToken: string) => Promise<void>
  clearError: () => void
  logout: () => void
}

async function loadSsoConfig(): Promise<SsoConfig> {
  try {
    return await api.ssoConfig()
  } catch {
    // A deployment with no SSO configured falls back to local password auth.
    return { enabled: false, issuer: null, clientId: null, redirectUri: null }
  }
}

/**
 * Brings the on-device agent up once the user is signed in.
 *
 * Deliberately not awaited: pairing talks to the API and starting the agent
 * spawns a process, and neither should hold up showing the app. A machine that
 * cannot run an agent still works with hosted models, so a failure here is
 * logged rather than surfaced as a sign-in error.
 */
function startAgentInBackground() {
  if (!isDesktopApp) return
  void ensureAgentRunning().catch((e) => {
    console.warn('The on-device agent could not be started:', e)
  })
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  status: 'loading',
  error: null,
  ssoConfig: null,
  offline: false,
  pendingVerificationEmail: null,

  /** Validates a persisted token on boot so a stale one does not linger. */
  restore: async () => {
    const ssoConfig = await loadSsoConfig()
    set({ ssoConfig })

    if (!getToken()) {
      set({ status: 'anonymous', user: null })
      return
    }
    try {
      const user = await api.me()
      setCachedUser(user)
      set({ user, status: 'authenticated', error: null, offline: false })
      startAgentInBackground()
    } catch (e) {
      // Only the API rejecting the token means the session is over. Being
      // unable to reach the API says nothing about whether it is valid, and
      // signing the user out would strand them from models sitting on their
      // own disk -- which is the whole point of running them locally.
      const cached = getCachedUser()
      if (isOffline(e) && cached) {
        set({ user: cached, status: 'authenticated', error: null, offline: true })
        startAgentInBackground()
        return
      }
      setToken(null)
      setCachedUser(null)
      set({ status: 'anonymous', user: null, offline: false })
    }
  },

  // Password sign-in is closed on the API the moment SSO is usable, so this
  // takes the SSO path whenever it's configured and only falls back to the
  // local endpoint for a deployment that has none.
  login: async (email, password) => {
    set({ error: null })
    const { ssoConfig } = get()
    try {
      if (ssoConfig?.enabled && ssoConfig.issuer) {
        // The browser can reach the SSO directly; the packaged app cannot,
        // because the SSO refuses its `tauri://` origin at the preflight. Both
        // end at the same exchange, so only the first hop differs.
        const response = isDesktopApp
          ? await api.ssoSignin(email, password)
          : await api.ssoExchange(
              await ssoClient.signin(ssoConfig.issuer, email, password),
            )
        setToken(response.accessToken)
        setCachedUser(response.user)
        set({ user: response.user, status: 'authenticated', offline: false })
        startAgentInBackground()
      } else {
        const response = await api.login(email, password)
        setToken(response.accessToken)
        setCachedUser(response.user)
        set({ user: response.user, status: 'authenticated', offline: false })
        startAgentInBackground()
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Sign-in failed' })
      throw e
    }
  },

  // An SSO sign-up does not return a session — the account is not usable
  // until the verification link is clicked — so this only records that a
  // verification email is on its way and leaves sign-in to the login form.
  register: async (email, name, password) => {
    set({ error: null, pendingVerificationEmail: null })
    const { ssoConfig } = get()
    try {
      if (ssoConfig?.enabled && ssoConfig.issuer) {
        const [firstName, ...rest] = name.trim().split(/\s+/)
        await ssoClient.signup(ssoConfig.issuer, {
          email,
          password,
          firstName: firstName || name.trim(),
          lastName: rest.join(' ') || undefined,
          callbackUrl: window.location.origin,
        })
        set({ pendingVerificationEmail: email })
      } else {
        const response = await api.register(email, name, password)
        setToken(response.accessToken)
        setCachedUser(response.user)
        set({ user: response.user, status: 'authenticated', offline: false })
        startAgentInBackground()
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Could not create the account' })
      throw e
    }
  },

  forgotPassword: async (email) => {
    set({ error: null })
    const { ssoConfig } = get()
    if (!ssoConfig?.enabled || !ssoConfig.issuer) {
      const message = 'Password reset is not available for this deployment.'
      set({ error: message })
      throw new Error(message)
    }
    try {
      await ssoClient.forgotPassword(ssoConfig.issuer, email, `${window.location.origin}/reset-password`)
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Could not send the reset email' })
      throw e
    }
  },

  resetPassword: async (token, newPassword) => {
    set({ error: null })
    const { ssoConfig } = get()
    if (!ssoConfig?.enabled || !ssoConfig.issuer) {
      const message = 'Password reset is not available for this deployment.'
      set({ error: message })
      throw new Error(message)
    }
    try {
      await ssoClient.resetPassword(ssoConfig.issuer, token, newPassword)
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Could not reset the password' })
      throw e
    }
  },

  resendVerification: async (email) => {
    const { ssoConfig } = get()
    if (!ssoConfig?.enabled || !ssoConfig.issuer) return
    try {
      await ssoClient.resendVerification(ssoConfig.issuer, email)
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Could not resend the verification email' })
      throw e
    }
  },

  completeSsoExchange: async (ssoToken) => {
    set({ error: null })
    try {
      const response = await api.ssoExchange(ssoToken)
      setToken(response.accessToken)
      setCachedUser(response.user)
        set({ user: response.user, status: 'authenticated', offline: false })
      startAgentInBackground()
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Could not complete sign-in' })
      throw e
    }
  },

  clearError: () => set({ error: null }),

  logout: () => {
    setToken(null)
    // Signing out is deliberate, so the cached identity goes with it.
    setCachedUser(null)
    set({ user: null, status: 'anonymous', offline: false })
  },
}))
